/**
 * @fileoverview Types for Arc Projects List feature
 */

/** Share type for project sharing */
export type ShareType = 'copy' | 'co_teacher'

/** Tutor returned by GET /api/arc_mosaico/tutors (same shape as ProjectAuthorResponse) */
export type Tutor = ProjectAuthorResponse

/** API response for project grade association */
export interface ProjectGradeResponse {
  id: string
  grade: {
    id: string
    name: string
  }
}

/** API response for project author (library only) - camelCase after api helper transforms */
export interface ProjectAuthorResponse {
  id: string
  firstName: string
  lastName: string
  email: string
}

/** API response for project co-teacher */
export interface ProjectTeacherResponse {
  id: string
  tutor: {
    id: string
    user: ProjectAuthorResponse
  }
}

/** Group school term info from API */
export interface GroupSchoolTermResponse {
  id: string
  name: string
}

/**
 * API response for a single project
 * Note: The api helper (src/helpers/api.ts) uses camelizeKeys from 'humps'
 * which automatically converts snake_case keys from backend to camelCase
 */
export interface ArcProjectResponse {
  id: string
  name: string
  status: string
  synopsis: string | null
  groupSchoolTermId: string | null
  schoolTermId: string | null
  groupSchoolTerm: GroupSchoolTermResponse | null
  kind: 'template' | 'application'
  updatedAt: string
  schoolId: string
  authorId: string
  projectGrades: ProjectGradeResponse[]
  author?: ProjectAuthorResponse
  projectTeachers?: ProjectTeacherResponse[]
  categories?: Category[]
}

export interface ProjectGrade {
  id: string
  grade: {
    id: string
    name: string
  }
}

/** Co-teacher info (flattened from API response) */
export interface CoTeacher {
  email: string
  firstName: string
  lastName: string
}

export interface ArcProject {
  id: string
  name: string
  status: ProjectStatus
  synopsis: string | null
  groupSchoolTermId: string | null
  schoolTermId: string | null
  groupName: string | null
  kind: 'template' | 'application'
  updatedAt: string
  schoolId: string
  authorId: string
  projectGrades: ProjectGrade[]
  authorName?: string
  coTeachers: CoTeacher[]
  categories: Category[]
}

/** Project status values */
export type ProjectStatus = 'draft' | 'published' | 'approved' | 'finished'

/** Display status for UI (maps to actual status) */
export type DisplayStatus = 'all' | 'draft' | 'assigned' | 'completed'

/** Tab options */
export type ProjectTab = 'my' | 'library'

/** Order by options */
export type OrderByOption = 'name' | 'status' | 'created_at'

/** Sort direction */
export type SortDirection = 'asc' | 'desc'

/** Grade range segments matching Figma design (lowercase to match backend contract) */
export type GradeRange = 'all' | 'k-2' | '3-5' | '6-8' | '9-12'

/** Category from GET /api/arc_mosaico/categories */
export interface Category {
  id: string
  name: string
  color: string
}

/** Query params for projects API */
export interface ProjectsQueryParams {
  name?: string
  status?: string
  order_by?: OrderByOption
  direction?: SortDirection
  page?: number
  limit?: number
  grade_id?: string
  grade_ids?: string[]
  grade_range?: GradeRange
  category_id?: string
}

/** Pagination metadata from Pagy (backend) */
export interface PaginationMeta {
  count: number      // Total items
  page: number       // Current page
  items: number      // Items per page
  pages: number      // Total pages
  last: number       // Last page number
  prev: number | null
  next: number | null
  from: number       // First item index on current page
  to: number         // Last item index on current page
}

/** API response with pagination */
export interface PaginatedResponse<T> {
  projects: T[]
  pagination: PaginationMeta
}

/** Status chip option */
export interface StatusOption {
  value: DisplayStatus
  label: string
}

/** Order by select option */
export interface OrderBySelectOption {
  value: OrderByOption
  label: string
  defaultDirection: SortDirection
}

/** Status display config (badge color, label) */
export interface StatusDisplayConfig {
  color: string
  labelKey: string
}

/** Map of status to display config */
export const STATUS_DISPLAY_MAP: Record<ProjectStatus, StatusDisplayConfig> = {
  draft: { color: 'gray', labelKey: 'arc_projects.status_draft' },
  published: { color: 'blue', labelKey: 'arc_projects.status_published' },
  approved: { color: 'blue', labelKey: 'arc_projects.status_assigned' },
  finished: { color: 'green', labelKey: 'arc_projects.status_completed' },
}

/** Map display status to API status */
export const DISPLAY_TO_API_STATUS: Record<DisplayStatus, string | undefined> = {
  all: undefined,
  draft: 'draft',
  assigned: 'assigned',
  completed: 'completed',
}

/** Default order by options */
export const ORDER_BY_OPTIONS: OrderBySelectOption[] = [
  { value: 'name', label: 'arc_projects.order_name', defaultDirection: 'asc' },
  { value: 'status', label: 'arc_projects.order_status', defaultDirection: 'asc' },
  { value: 'created_at', label: 'arc_projects.order_created', defaultDirection: 'desc' },
]

/** Status options for chips */
export const STATUS_OPTIONS: StatusOption[] = [
  { value: 'all', label: 'arc_projects.status_all' },
  { value: 'draft', label: 'arc_projects.status_draft' },
  { value: 'assigned', label: 'arc_projects.status_assigned' },
  { value: 'completed', label: 'arc_projects.status_completed' },
]

/**
 * Assign Project Types
 */

/** Request body for assigning a project */
export interface AssignProjectRequest {
  group_school_term_id: string
  student_ids: string[]
  start_date?: string
}

/** Response from assign project endpoint */
export interface AssignProjectResponse {
  success: boolean
  project?: ArcProjectResponse
  error?: string
}
